/**
 * ApexConnectionSection — Settings card for the Apex controller
 * connection (SPEC-web-app-conversion Phase 4).
 *
 * The operator enters the Apex IP/hostname, username, and password.
 * "Test" runs a one-shot probe on the Pi without touching the running
 * poller; "Save & connect" persists to the Pi (`./data/apex-config.json`)
 * and hot-restarts the poller. A status badge polls
 * `GET /api/apex/status` every 5 s while the section is mounted:
 *
 *   green  connected     — fresh snapshot within ~3 poll intervals
 *   amber  connecting    — started, first poll not landed yet
 *   amber  unreachable   — network error / stale snapshot
 *   red    auth failed   — Apex rejected the credentials (poller stops
 *                          by design until a new config is saved)
 *   gray   not configured
 */
import { useCallback, useEffect, useState } from 'react';

import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import {
  apexConfigApi,
  type ApexStatus,
  type ApexTestResult,
} from '../../lib/apex-config-api';

const STATUS_POLL_MS = 5000;

interface BadgeView {
  label: string;
  className: string;
}

function badgeFor(state: ApexStatus['state'] | 'unknown'): BadgeView {
  switch (state) {
    case 'connected':
      return { label: 'Connected', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' };
    case 'connecting':
      return { label: 'Connecting…', className: 'bg-amber-500/15 text-amber-300 border-amber-500/40' };
    case 'unreachable':
      return { label: 'Unreachable', className: 'bg-amber-500/15 text-amber-300 border-amber-500/40' };
    case 'auth-failed':
      return { label: 'Auth failed', className: 'bg-red-500/15 text-red-300 border-red-500/40' };
    case 'disabled':
      return { label: 'Not configured', className: 'bg-reef-700/40 text-reef-300 border-reef-600' };
    case 'unknown':
      return { label: 'Pi unreachable', className: 'bg-reef-700/40 text-reef-300 border-reef-600' };
  }
}

export function ApexConnectionSection() {
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hasStoredPassword, setHasStoredPassword] = useState(false);
  const [status, setStatus] = useState<ApexStatus | null>(null);
  const [statusKnown, setStatusKnown] = useState(true);
  const [busy, setBusy] = useState<'test' | 'save' | 'disconnect' | null>(null);
  const [testResult, setTestResult] = useState<ApexTestResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    const res = await apexConfigApi.getStatus();
    if (res.ok) {
      setStatus(res.value.status);
      setStatusKnown(true);
    } else {
      setStatusKnown(false);
    }
  }, []);

  // Initial load: stored config + status; then poll status while mounted.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cfg = await apexConfigApi.getConfig();
      if (!cancelled && cfg.ok && cfg.value.config) {
        setHost(cfg.value.config.host);
        setUsername(cfg.value.config.username);
        setHasStoredPassword(cfg.value.config.hasPassword);
      }
      await refreshStatus();
    })();
    const timer = setInterval(() => {
      void refreshStatus();
    }, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [refreshStatus]);

  const canSubmit = host.trim().length > 0 && username.trim().length > 0 && busy === null;

  function buildInput() {
    const input: { host: string; username: string; password?: string } = {
      host: host.trim(),
      username: username.trim(),
    };
    // Empty password field + a password already stored on the Pi means
    // "keep the stored one" — only send when the operator typed something.
    if (password.length > 0 || !hasStoredPassword) {
      input.password = password;
    }
    return input;
  }

  async function handleTest() {
    setBusy('test');
    setMessage(null);
    setTestResult(null);
    const res = await apexConfigApi.test(buildInput());
    if (res.ok) {
      setTestResult(res.value.test);
    } else {
      setMessage(`Could not reach the Pi to run the test: ${res.error}`);
    }
    setBusy(null);
  }

  async function handleSave() {
    setBusy('save');
    setMessage(null);
    setTestResult(null);
    const res = await apexConfigApi.putConfig(buildInput());
    if (res.ok) {
      setStatus(res.value.status);
      setStatusKnown(true);
      setHasStoredPassword(true);
      setPassword('');
      setMessage('Saved. Monitoring will connect within a few seconds.');
    } else {
      setMessage(`Save failed: ${res.error}`);
    }
    setBusy(null);
  }

  async function handleDisconnect() {
    if (!window.confirm('Stop monitoring this Apex and forget its connection settings?')) {
      return;
    }
    setBusy('disconnect');
    setMessage(null);
    setTestResult(null);
    const res = await apexConfigApi.deleteConfig();
    if (res.ok) {
      setStatus(res.value.status);
      setStatusKnown(true);
      setHost('');
      setUsername('');
      setPassword('');
      setHasStoredPassword(false);
      setMessage('Disconnected.');
    } else {
      setMessage(`Disconnect failed: ${res.error}`);
    }
    setBusy(null);
  }

  const badge = badgeFor(statusKnown ? (status?.state ?? 'disabled') : 'unknown');

  return (
    <Card className="mt-6" data-testid="settings-apex-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Apex connection</CardTitle>
          <span
            className={`text-[11px] uppercase tracking-wide border rounded-full px-2.5 py-0.5 ${badge.className}`}
            data-testid="apex-status-badge"
          >
            {badge.label}
          </span>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-xs text-reef-300 mb-4">
          Enter your Apex controller&apos;s address on your home network. The Pi
          polls it read-only — ReefApp never switches outlets. Settings are
          stored on the Pi, so every device sees the same connection.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
          <label className="text-sm text-reef-200 flex flex-col gap-1">
            Apex IP address or hostname
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="192.168.0.60"
              className="rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
              data-testid="apex-host-input"
            />
          </label>
          <label className="text-sm text-reef-200 flex flex-col gap-1">
            Username
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
              data-testid="apex-username-input"
            />
          </label>
          <label className="text-sm text-reef-200 flex flex-col gap-1 sm:col-span-2">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={hasStoredPassword ? '•••••• (saved — leave blank to keep)' : ''}
              className="rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
              data-testid="apex-password-input"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!canSubmit}
            onClick={() => void handleTest()}
            data-testid="apex-test-button"
          >
            {busy === 'test' ? 'Testing…' : 'Test connection'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canSubmit}
            onClick={() => void handleSave()}
            data-testid="apex-save-button"
          >
            {busy === 'save' ? 'Saving…' : 'Save & connect'}
          </Button>
          {status?.configured ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy !== null}
              onClick={() => void handleDisconnect()}
              data-testid="apex-disconnect-button"
            >
              Disconnect
            </Button>
          ) : null}
        </div>

        {testResult ? (
          <p className="mt-3 text-sm" data-testid="apex-test-result">
            {testResult.result === 'connected' ? (
              <span className="text-emerald-300">
                Connection OK — found {testResult.probeCount} probes and {testResult.outletCount}{' '}
                outlets.
              </span>
            ) : testResult.result === 'auth-failed' ? (
              <span className="text-red-300">
                The Apex rejected the username or password.
              </span>
            ) : (
              <span className="text-amber-300">
                Could not reach the Apex at that address
                {testResult.error ? ` (${testResult.error})` : ''}.
              </span>
            )}
          </p>
        ) : null}

        {message ? (
          <p className="mt-3 text-sm text-reef-200" data-testid="apex-message">
            {message}
          </p>
        ) : null}

        {status?.state === 'connected' ? (
          <p className="mt-3 text-xs text-reef-400" data-testid="apex-connected-summary">
            Watching {status.host} — {status.probeCount} probes, {status.outletCount} outlets.
          </p>
        ) : null}
        {status?.state === 'auth-failed' ? (
          <p className="mt-3 text-xs text-red-300/80">
            Polling is paused until a working password is saved (this protects the
            Apex from lockout).
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
